import {z} from 'zod';
import {languageTagZod} from '../../schemas';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'create-translation-exclusions' as const;

const pageFieldTarget = z.object({
  kind: z.literal('page-field'),
  pageId: z.string().describe('The id of the page this rule applies to'),
  field: z
    .enum([
      'canvas',
      'title',
      'slug',
      'seoTitle',
      'seoDesc',
      'ogTitle',
      'ogDesc',
    ])
    .describe('Which page field to exclude from translation'),
});

const collectionFieldTarget = z.object({
  kind: z.literal('collection-field'),
  collectionId: z
    .string()
    .describe('The id of the CMS collection this rule applies to'),
  fieldSlug: z
    .string()
    .describe(
      'The field slug within the collection to exclude from translation'
    ),
});

const componentPropTarget = z.object({
  kind: z.literal('component-prop'),
  componentId: z
    .string()
    .describe(
      "The id of the component this rule applies to, from list-site-components. This is the component's GUID, not a Mongo ObjectId."
    ),
  propId: z
    .string()
    .describe(
      "The id of the component property to exclude, from get-component-properties. Use 'canvas' to exclude the whole component instead of a single property."
    ),
  propKind: z
    .enum(['canvas', 'textContent', 'richText'])
    .describe(
      "The kind of the property being excluded -- 'canvas' when propId is 'canvas', otherwise the kind reported by get-component-properties for that property"
    ),
});

const argumentZod = z.object({
  items: z
    .array(
      z.object({
        target: z.discriminatedUnion('kind', [
          pageFieldTarget,
          collectionFieldTarget,
          componentPropTarget,
        ]),
        languages: z
          .array(languageTagZod)
          .optional()
          .describe(
            "BCP-47 language tags this rule applies to (e.g. 'es', 'pt-BR') -- get valid tags via the list-target-languages tool first. Omit or leave empty to apply to all target languages."
          ),
        triggers: z
          .array(z.enum(['auto', 'manual']))
          .optional()
          .describe(
            'Which translation triggers this rule applies to. Omit or leave empty to apply to both auto and manual translation.'
          ),
      })
    )
    .min(1)
    .max(250)
    .describe('Translation exclusion rules to create.'),
});

export const createTranslationExclusionsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create translation exclusion rules',
  prettyStatus: {
    executing: 'Creating translation exclusions…',
    executed: 'Created translation exclusions',
    failed: 'Creating translation exclusions failed',
  },
  family: 'localization',
  llmTool: {
    name: toolName,
    description:
      'Creates one or more translation exclusion rules, marking page fields, CMS collection fields, or component properties (or whole components) as excluded from translation for the current site. If a target already has a rule, it is left as-is -- no duplicate is created and any language or trigger scoping already set on it is preserved.',
    inputSchema: argumentZod,
  },
  type: 'server',
  requiredScopes: ['sites:write'],
  permission: [{resource: 'Site', action: 'update'}],
  confirmBeforeExecuting: true,
});

export type CreateTranslationExclusionsArgumentType = z.infer<
  typeof argumentZod
>;
